/**
 * KAN-725: upload options of the campaign list page.
 *
 * Google Sheets link (instead of a file), how many new rows to take, and
 * whether to skip rows that are already clients. Duplicates of the lists
 * already uploaded are always skipped by the backend.
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { presetLabel } from '@/lib/uploadMapping';
import type { UploadPreset } from '@/types/dataset';

export interface UploadOptionsValue {
  sheetUrl: string;
  limit: string;
  skipExistingClients: boolean;
}

interface UploadOptionsProps {
  value: UploadOptionsValue;
  onChange: (value: UploadOptionsValue) => void;
}

interface UploadSheetUrlInputProps extends UploadOptionsProps {
  /** KAN-727: recent uploads with a Google Sheets link (latest first). */
  recent?: UploadPreset[];
  onPickRecent?: (preset: UploadPreset) => void;
}

export function UploadSheetUrlInput({
  value,
  onChange,
  recent = [],
  onPickRecent,
}: UploadSheetUrlInputProps): JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor="upload-sheet-url">или ссылка на Google-таблицу</Label>
      <Input
        id="upload-sheet-url"
        data-testid="upload-sheet-url"
        type="url"
        inputMode="url"
        placeholder="https://docs.google.com/spreadsheets/d/..."
        value={value.sheetUrl}
        onChange={(e) => onChange({ ...value, sheetUrl: e.target.value })}
      />
      {recent.length > 0 && (
        <NativeSelect
          aria-label="Недавние таблицы"
          data-testid="upload-recent-sheets"
          value=""
          placeholder="Недавние таблицы"
          options={recent.map((preset) => ({ value: preset.id, label: presetLabel(preset) }))}
          onChange={(e) => {
            const picked = recent.find((preset) => preset.id === e.target.value);
            if (picked) onPickRecent?.(picked);
          }}
        />
      )}
      <p className="text-xs text-gray-400">
        Таблица должна быть открыта по ссылке (Читатель). Берётся первый лист или
        лист из ссылки.
      </p>
    </div>
  );
}

export function UploadOptions({ value, onChange }: UploadOptionsProps): JSX.Element {
  return (
    <div data-testid="upload-options" className="bg-white border rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
        <Label htmlFor="upload-limit">Сколько загрузить</Label>
        <Input
          id="upload-limit"
          data-testid="upload-limit"
          type="number"
          min={1}
          step={1}
          placeholder="все"
          value={value.limit}
          onChange={(e) => onChange({ ...value, limit: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <Checkbox
          data-testid="upload-skip-clients"
          checked={value.skipExistingClients}
          onCheckedChange={(checked) => onChange({ ...value, skipExistingClients: checked })}
        />
        Не загружать тех, кто уже есть среди клиентов
      </label>
      <p data-testid="upload-options-hint" className="text-xs text-gray-500">
        Дубли с уже загруженными списками пропускаются автоматически; берутся
        первые N новых строк файла.
      </p>
    </div>
  );
}
